// ============================================================
// Notifications client (Brief v2 · Task 7).
//
// In-app notification feed backed by public.notifications. The header bell
// polls the unread count; opening it lists recent items and marks them read.
// Producers (e.g. "NCR raised against a contractor") call notifyMembers(),
// which fans a row out to every member of the project holding a given role.
//
// Email delivery is a thin add-on: an `notify-email` Edge Function can read
// new rows and post them through Resend/SendGrid — the in-app row is the
// source of truth, so email is optional and non-blocking.
// ============================================================
import { supabase } from "./supabase";
import { getProjectId } from "./persistence";

export interface AppNotification {
  id: number;
  type: string;
  entity_id: string | null;
  message: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

export async function fetchNotifications(limit = 30): Promise<AppNotification[]> {
  if (!supabase) return [];
  try {
    const { data, error } = await supabase
      .from("notifications")
      .select("id, type, entity_id, message, link, read_at, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) return [];
    return (data || []) as AppNotification[];
  } catch {
    return [];
  }
}

export async function unreadCount(): Promise<number> {
  if (!supabase) return 0;
  try {
    const { count } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .is("read_at", null);
    return count || 0;
  } catch {
    return 0;
  }
}

export async function markRead(id: number): Promise<void> {
  if (!supabase) return;
  try {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
  } catch {
    /* ignore */
  }
}

export async function markAllRead(): Promise<void> {
  if (!supabase) return;
  try {
    await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .is("read_at", null);
  } catch {
    /* ignore */
  }
}

/** Fan a notification out to every member of the active project holding one of
 *  `roles` (e.g. ["contractor"] when an NCR is raised). Best-effort. */
export async function notifyMembers(
  roles: string[],
  type: string,
  entityId: string,
  message: string,
  link = "/punch",
): Promise<void> {
  if (!supabase) return;
  try {
    await supabase.rpc("notify_members", {
      p_project: getProjectId(),
      p_roles: roles,
      p_type: type,
      p_entity_id: entityId,
      p_message: message,
      p_link: link,
    });
  } catch {
    /* offline — skip */
  }
}
